import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { messaging, getToken as getFCMToken, isBrowserSupported } from "../../../firebase-config";
import { registerDevice, DeviceRegistrationParams, unregisterDevice } from "../network/network";
import { getI18n } from "react-i18next";
import { v4 } from "uuid";
import { getDeviceId, registerServiceWorker } from "../../../utils/service-worker";
import { get } from "lodash";
import storage from "redux-persist/es/storage";

export interface NotificationState {
  isLoading: boolean;
  pushToken?: string;
  error?: string | null;
}

const initialState: NotificationState = {
  isLoading: false,
  error: null,
};

export const notificationPersistConfig = {
  key: "notification",
  storage,
};

export const registerPushNotification = createAsyncThunk(
  "notification/register",
  async (_, { rejectWithValue }) => {
    try {
      if (!isBrowserSupported()) {
        console.warn("Push notifications are not supported in this browser");
        return;
      }

      // Request permission
      const permission = Notification.permission;
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }

      const registration = await registerServiceWorker();
      if (!registration) {
        throw new Error("Service Worker registration failed");
      }

      if (!messaging) {
        throw new Error("Messaging is not initialized");
      }

      const pushToken = await getFCMToken(messaging, {
        vapidKey: get(window, "envConfig.REACT_APP_PUBLIC_FIREBASE_VAPID_KEY", ""),
        serviceWorkerRegistration: registration,
      });

      const deviceId = getDeviceId();
      const params: DeviceRegistrationParams = {
        platform: "web",
        pnToken: pushToken as string,
        lang: getI18n().language,
        deviceId,
      };

      await registerDevice(params);
      return pushToken;
    } catch (error: any) {
      console.error("Error registering device through api:", error);
      return rejectWithValue(error.message);
    }
  },
);
export const unregisterNotificationDevice = createAsyncThunk(
  "notification/unregisterNotificationDevice",
  async (_, { rejectWithValue }) => {
    try {
      await unregisterDevice("web");
      return "";
    } catch (error: any) {
      console.error("Error unregistering device:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(registerPushNotification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerPushNotification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pushToken = action.payload as string;
      })
      .addCase(registerPushNotification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(unregisterNotificationDevice.fulfilled, (state) => {
        state.isLoading = false;
        state.pushToken = "";
      })
      .addCase(unregisterNotificationDevice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default notificationSlice.reducer;
